import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import {
  CalendarDays,
  Compass,
  FileText,
  LayoutDashboard,
  Loader2,
  Music,
  Radio,
  Search,
  Settings,
  Upload,
  Users,
  X,
  type LucideIcon,
} from "lucide-react";
import { listFestivals, listRiders } from "@/api/client";
import { useAuthStore } from "@/stores/auth";
import { useActiveOrgId } from "@/hooks/useActiveOrg";
import { useFocusTrap } from "@/hooks/useFocusTrap";
import { useIsMobileViewport } from "@/hooks/useMediaQuery";
import { cn } from "@/lib/utils";
import { Icon, StatusBadge, type RiderStatus } from "@/components/ds";

/**
 * Global ⌘K command palette: client-side search over festivals (name,
 * location), riders (artist, original filename, type) and app pages, with
 * grouped results and full keyboard navigation. Data comes from the same
 * query keys the list pages use, so the palette rides their cache and only
 * fetches while open.
 */

interface PageEntry {
  label: string;
  to: string;
  icon: LucideIcon;
}

const PAGES: PageEntry[] = [
  { label: "Explore Trace — interactive feature guide", to: "/learn", icon: Compass },
  { label: "Dashboard", to: "/dashboard", icon: LayoutDashboard },
  { label: "Festivals", to: "/festivals", icon: CalendarDays },
  { label: "Riders", to: "/riders", icon: FileText },
  { label: "Upload rider", to: "/riders/upload", icon: Upload },
  { label: "Crew", to: "/crew", icon: Users },
  { label: "Live — public timetables", to: "/live", icon: Radio },
  { label: "Settings", to: "/settings", icon: Settings },
];

// Rider lifecycle → the five DS status classes (extracting → processing).
const RIDER_STATUS: Record<string, { s: RiderStatus; label: string }> = {
  pending: { s: "pending", label: "Pending" },
  extracting: { s: "processing", label: "Extracting" },
  extracted: { s: "extracted", label: "Extracted" },
  approved: { s: "approved", label: "Approved" },
  failed: { s: "failed", label: "Failed" },
};

function RiderStatusChip({ status }: { status: string }) {
  const m = RIDER_STATUS[status] ?? { s: "pending" as RiderStatus, label: status };
  return <StatusBadge status={m.s} label={m.label} />;
}

type PaletteItem =
  | { kind: "page"; key: string; label: string; to: string; icon: LucideIcon }
  | { kind: "festival"; key: string; label: string; to: string; meta: string | null; color: string | null }
  | { kind: "rider"; key: string; label: string; to: string; meta: string | null; status: string };

interface PaletteGroup {
  title: string;
  items: PaletteItem[];
}

export function CommandPalette({ open, onClose }: { open: boolean; onClose: () => void }) {
  const navigate = useNavigate();
  const activeOrgId = useActiveOrgId();
  const hasArtistProfile = useAuthStore((s) => s.user?.has_artist_profile ?? false);
  const isMobile = useIsMobileViewport();
  const [query, setQuery] = useState("");
  const [active, setActive] = useState(0);
  const panelRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  useFocusTrap(panelRef, open);

  // Same query keys as FestivalListPage / RiderListPage → shared cache.
  const { data: festivals, isLoading: loadingFestivals } = useQuery({
    queryKey: ["festivals", activeOrgId],
    queryFn: () => listFestivals(activeOrgId),
    enabled: open,
    staleTime: 30_000,
  });
  const { data: riders, isLoading: loadingRiders } = useQuery({
    queryKey: ["riders", "", activeOrgId],
    queryFn: () => listRiders(undefined, activeOrgId),
    enabled: open,
    staleTime: 30_000,
  });

  useEffect(() => {
    if (!open) return;
    setQuery("");
    setActive(0);
    const frame = window.requestAnimationFrame(() => inputRef.current?.focus());
    return () => window.cancelAnimationFrame(frame);
  }, [open]);

  useEffect(() => {
    setActive(0);
  }, [query]);

  const groups = useMemo<PaletteGroup[]>(() => {
    const q = query.trim().toLowerCase();
    const matches = (...vals: Array<string | null | undefined>) =>
      q === "" || vals.some((v) => (v ?? "").toLowerCase().includes(q));

    const allPages: PageEntry[] = hasArtistProfile
      ? [...PAGES, { label: "My Schedule", to: "/my-schedule", icon: Music }]
      : PAGES;
    const pages: PaletteItem[] = allPages.filter((p) => matches(p.label)).map((p) => ({
      kind: "page",
      key: `page-${p.to}`,
      label: p.label,
      to: p.to,
      icon: p.icon,
    }));

    const fests: PaletteItem[] = (festivals ?? [])
      .filter((f) => matches(f.name, f.location))
      .slice(0, q ? 6 : 5)
      .map((f) => ({
        kind: "festival",
        key: `festival-${f.id}`,
        label: f.name,
        to: `/festivals/${f.id}`,
        meta: f.location,
        color: f.color,
      }));

    const rs: PaletteItem[] = (riders ?? [])
      .filter((r) => matches(r.artist_name, r.original_filename, r.rider_type))
      .slice(0, q ? 8 : 5)
      .map((r) => ({
        kind: "rider",
        key: `rider-${r.id}`,
        label: r.artist_name,
        to: `/riders/${r.id}/review`,
        meta: r.original_filename,
        status: r.status,
      }));

    const out: PaletteGroup[] = [];
    if (fests.length > 0) out.push({ title: "Festivals", items: fests });
    if (rs.length > 0) out.push({ title: "Riders", items: rs });
    if (pages.length > 0) out.push({ title: "Pages", items: pages });
    return out;
  }, [query, festivals, riders, hasArtistProfile]);

  const flat = useMemo(() => groups.flatMap((g) => g.items), [groups]);

  const go = useCallback(
    (item: PaletteItem) => {
      onClose();
      navigate(item.to);
    },
    [navigate, onClose],
  );

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive((a) => Math.min(a + 1, Math.max(0, flat.length - 1)));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((a) => Math.max(a - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      const item = flat[active];
      if (item) go(item);
    } else if (e.key === "Escape") {
      e.preventDefault();
      onClose();
    }
  };

  // Keep the active row visible while arrowing through a long list.
  useEffect(() => {
    listRef.current
      ?.querySelector(`[data-idx="${active}"]`)
      ?.scrollIntoView({ block: "nearest" });
  }, [active]);

  const loading = loadingFestivals || loadingRiders;
  let idx = -1;

  return createPortal(
    <>
      <div
        className={cn("scrim", open && "scrim--open")}
        aria-hidden="true"
        onClick={onClose}
      />
      <div
        id="global-search-dialog"
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label="Global search"
        aria-hidden={!open}
        onKeyDown={onKeyDown}
        className={cn(
          isMobile
            ? "command-palette fixed inset-0 z-[90] h-[100dvh] w-screen overflow-hidden border-0 bg-panel pt-[env(safe-area-inset-top)] transition-[opacity,transform] duration-150 motion-reduce:transition-none"
            : "command-palette fixed left-1/2 top-[14vh] z-[90] w-[min(580px,calc(100vw-32px))] -translate-x-1/2 overflow-hidden rounded-[14px] border border-line bg-panel transition-[opacity,transform] duration-150 motion-reduce:transition-none",
          open ? "visible scale-100 opacity-100" : "invisible pointer-events-none scale-[0.98] opacity-0",
        )}
        style={{ boxShadow: "var(--tr-shadow-drawer)" }}
      >
        <div className="flex items-center gap-2.5 border-b border-line-2 px-3.5 py-3">
          <Icon icon={Search} size={16} className="flex-none text-ink-3" />
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search festivals, riders, artists, pages…"
            aria-label="Search"
            role="combobox"
            aria-expanded={open}
            aria-controls="command-palette-results"
            aria-activedescendant={flat.length > 0 ? `cp-item-${active}` : undefined}
            className="flex-1 bg-transparent text-base text-ink outline-none placeholder:text-ink-4 md:text-[13.5px]"
          />
          {loading && <Loader2 className="h-3.5 w-3.5 flex-none animate-spin text-ink-4" />}
          <span className="kbd hidden sm:inline">esc</span>
          <button
            type="button"
            className="iconbtn sm:hidden"
            onClick={onClose}
            aria-label="Close search"
          >
            <Icon icon={X} size={18} />
          </button>
        </div>

        <div
          ref={listRef}
          id="command-palette-results"
          role="listbox"
          aria-label="Search results"
          className={cn(
            "command-palette__results overflow-y-auto pb-1.5",
            isMobile ? "max-h-[calc(100dvh-136px-env(safe-area-inset-top))]" : "max-h-[48vh]",
          )}
        >
          {flat.length === 0 ? (
            <div className="px-3.5 py-8 text-center text-[13px] text-ink-3">
              {loading ? "Loading…" : <>No matches for &ldquo;{query}&rdquo;</>}
            </div>
          ) : (
            groups.map((g) => (
              <div key={g.title}>
                <div className="px-3.5 pb-1 pt-2.5 text-[10px] font-bold uppercase tracking-[0.08em] text-ink-4">
                  {g.title}
                </div>
                {g.items.map((item) => {
                  idx += 1;
                  const i = idx;
                  return (
                    <button
                      key={item.key}
                      type="button"
                      id={`cp-item-${i}`}
                      data-idx={i}
                      role="option"
                      aria-selected={i === active}
                      onClick={() => go(item)}
                      onMouseEnter={() => setActive(i)}
                      className={cn(
                        "flex w-full items-center gap-2.5 px-3.5 py-2 text-left text-[13px] transition-colors",
                        i === active ? "bg-panel-2 text-ink" : "text-ink-2",
                      )}
                    >
                      {item.kind === "page" && (
                        <Icon icon={item.icon} size={15} className="flex-none text-ink-3" />
                      )}
                      {item.kind === "festival" && (
                        <i
                          className="h-2.5 w-2.5 flex-none rounded-[3px]"
                          style={{ background: item.color ?? "var(--tr-accent)" }}
                        />
                      )}
                      {item.kind === "rider" && (
                        <Icon icon={FileText} size={15} className="flex-none text-ink-3" />
                      )}
                      <span className="min-w-0 flex-1 truncate">
                        {item.label}
                        {item.kind !== "page" && item.meta && (
                          <span className="ml-2 text-[11px] text-ink-4">{item.meta}</span>
                        )}
                      </span>
                      {item.kind === "rider" && <RiderStatusChip status={item.status} />}
                    </button>
                  );
                })}
              </div>
            ))
          )}
        </div>

        <div className="flex items-center gap-3 border-t border-line-2 px-3.5 py-2 text-[10px] text-ink-4 max-sm:hidden">
          <span>
            <span className="kbd">↑↓</span> navigate
          </span>
          <span>
            <span className="kbd">↵</span> open
          </span>
          <span>
            <span className="kbd">esc</span> close
          </span>
        </div>
      </div>
    </>,
    document.body,
  );
}
